import {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {Package} from 'lucide-react'

export interface ProductThumbnailDetails {
  status?: string
  statusLabel?: string
  estoqueAtual?: number
  cmd?: number
  cobertura?: number
  leadTime?: number
  estoqueAlvo?: number
  pedidoSugerido?: number
  fornecedor?: string
}

interface ProductThumbnailProps {
  src?: string
  alt: string
  ean?: string
  size?: 'sm' | 'md'
  details?: ProductThumbnailDetails
  hoverTargetRef?: {current: HTMLElement | null}
  hoverParent?: boolean
}

const sizeClasses = {
  sm: 'h-10 w-10',
  md: 'h-12 w-12',
}
const statusClasses: Record<string, string> = {
  ruptura: 'border-red-200 bg-red-50 text-red-700', iminente: 'border-orange-200 bg-orange-50 text-orange-700',
  minimo: 'border-amber-200 bg-amber-50 text-amber-700', critica: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  excesso: 'border-blue-200 bg-blue-50 text-blue-700', saudavel: 'border-emerald-200 bg-emerald-50 text-emerald-700',
}
function formatValue(value: number, digits = 0) {
  return new Intl.NumberFormat('pt-BR', {minimumFractionDigits: digits, maximumFractionDigits: digits}).format(value)
}

export function ProductThumbnail({
  src,
  alt,
  ean,
  size = 'md',
  details,
  hoverTargetRef,
  hoverParent = false,
}: ProductThumbnailProps) {
  const [failed, setFailed] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [previewPosition, setPreviewPosition] = useState({left: 16, top: 16})

  const [anchorPosition, setAnchorPosition] = useState({x: 16, y: 16})
  const previewRef = useRef<HTMLDivElement>(null)
  const thumbnailRef = useRef<HTMLDivElement>(null)
  const positionPreview = (clientX: number, clientY: number) => {
    const gap = 16
    const margin = 12
    const previewWidth = previewRef.current?.offsetWidth || 360
    const previewHeight = previewRef.current?.offsetHeight || (details ? 430 : ean ? 340 : 320)
    const fitsRight = clientX + gap + previewWidth <= window.innerWidth - margin
    const fitsBelow = clientY + gap + previewHeight <= window.innerHeight - margin
    setPreviewPosition({
      left: fitsRight ? clientX + gap : Math.max(margin, clientX - gap - previewWidth),
      top: fitsBelow ? clientY + gap : Math.max(margin, clientY - gap - previewHeight),
    })
  }

  const updatePreviewPosition = (clientX: number, clientY: number) => {
    setAnchorPosition({x: clientX, y: clientY})
    positionPreview(clientX, clientY)
  }

  const hasImage = Boolean(src && !failed)

  useEffect(() => {
    const target = hoverParent ? thumbnailRef.current?.parentElement : hoverTargetRef?.current
    if (!target || !hasImage) return

    const showPreview = (event: MouseEvent) => {
      updatePreviewPosition(event.clientX, event.clientY)
      setExpanded(true)
    }
    const hidePreview = () => setExpanded(false)

    target.addEventListener('mouseenter', showPreview)
    target.addEventListener('mousemove', showPreview)
    target.addEventListener('mouseleave', hidePreview)

    return () => {
      target.removeEventListener('mouseenter', showPreview)
      target.removeEventListener('mousemove', showPreview)
      target.removeEventListener('mouseleave', hidePreview)
    }
  }, [hoverTargetRef, hoverParent, hasImage, src])

  useLayoutEffect(() => {
    if (expanded) positionPreview(anchorPosition.x, anchorPosition.y)
  }, [expanded])

  useEffect(() => {
    setFailed(false)
    setExpanded(false)
  }, [src])

  return (
    <>
      <div
        ref={thumbnailRef}
        className={`${sizeClasses[size]} relative flex flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm`}
        onMouseEnter={(event) => {
          if (!hasImage) return
          updatePreviewPosition(event.clientX, event.clientY)
          setExpanded(true)
        }}
        onMouseMove={(event) => {
          if (expanded) updatePreviewPosition(event.clientX, event.clientY)
        }}
        onMouseLeave={() => {
          if (!hoverTargetRef && !hoverParent) setExpanded(false)
        }}
        onFocus={(event) => {
          if (!hasImage) return
          const rect = event.currentTarget.getBoundingClientRect()
          updatePreviewPosition(rect.right, rect.top)
          setExpanded(true)
        }}
        onBlur={() => setExpanded(false)}
        tabIndex={hasImage ? 0 : -1}
        aria-label={hasImage ? `Visualizar detalhes de ${alt}` : `Imagem indisponível para ${alt}`}
      >
        {hasImage ? (
          <img
            src={src}
            alt={alt}
            loading='lazy'
            referrerPolicy='no-referrer'
            className='h-full w-full object-contain p-1'
            onError={() => {
              setFailed(true)
              setExpanded(false)
            }}
          />
        ) : (
          <Package className='h-5 w-5 text-gray-300' aria-hidden='true' />
        )}
      </div>

      {hasImage &&
        expanded &&
        typeof document !== 'undefined' &&
        createPortal(
          <div ref={previewRef} role='tooltip' style={previewPosition}
            className='pointer-events-none fixed z-[30000] w-[360px] max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-teal-100 bg-white shadow-2xl ring-1 ring-slate-900/5'>
            <div className='flex h-52 w-full items-center justify-center bg-gradient-to-b from-slate-50 to-white p-3'>
              <img src={src} alt={alt} referrerPolicy='no-referrer' className='h-full w-full object-contain' onError={() => { setFailed(true); setExpanded(false) }} />
            </div>
            <div className='border-t border-slate-100 p-4'>
              <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'><p className='line-clamp-2 text-sm font-semibold leading-5 text-slate-900'>{alt}</p>{ean && <p className='mt-1 font-mono text-[11px] text-slate-500'>EAN {ean}</p>}</div>
                {details?.status && <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${statusClasses[details.status] || 'border-slate-200 bg-slate-50 text-slate-600'}`}>{details.statusLabel || details.status}</span>}
              </div>
              {details?.fornecedor && <div className='mt-3 rounded-lg border border-teal-100 bg-teal-50/70 px-3 py-2'><p className='text-[10px] font-semibold uppercase tracking-wide text-teal-700'>Fornecedor</p><p className='mt-0.5 truncate text-xs font-medium text-slate-800'>{details.fornecedor}</p></div>}
              {details && <div className='mt-3 grid grid-cols-3 gap-2'>
                {details.estoqueAtual !== undefined && <PreviewMetric label='Estoque' value={formatValue(details.estoqueAtual)} />}
                {details.cmd !== undefined && <PreviewMetric label='CMD' value={formatValue(details.cmd, 2)} suffix='/dia' />}
                {details.cobertura !== undefined && <PreviewMetric label='Cobertura' value={formatValue(details.cobertura, 1)} suffix=' dias' />}
                {details.leadTime !== undefined && <PreviewMetric label='Lead time' value={formatValue(details.leadTime)} suffix=' dias' />}
                {details.estoqueAlvo !== undefined && <PreviewMetric label='Estoque alvo' value={formatValue(details.estoqueAlvo)} />}
                {details.pedidoSugerido !== undefined && <PreviewMetric label='Pedido sugerido' value={formatValue(details.pedidoSugerido)} />}
              </div>}
            </div>
          </div>,          document.body
        )}
    </>
  )
}
function PreviewMetric({label, value, suffix = ''}: {label: string; value: string; suffix?: string}) {
  return <div className='rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2'>
    <p className='truncate text-[9px] font-semibold uppercase tracking-wide text-slate-500'>{label}</p>
    <p className='mt-0.5 whitespace-nowrap text-xs font-bold text-slate-900'>{value}<span className='ml-0.5 text-[9px] font-medium text-slate-500'>{suffix}</span></p>
  </div>
}
